/**
 * Dynamic Grid Strategy
 * Creates multiple chase orders at different tick distances
 *
 * Features:
 * - Up to 3 levels per side (6 orders total)
 * - Auto-multiplied tick distance (e.g., 10 -> 10, 20, 30 ticks)
 * - Range bounds: If price exits range, chase stops
 * - Partial fills: When one level fills, others continue
 * - State persistence across page refresh
 *
 * Uses ChaseUtils for shared functionality (order verification, position checks)
 */

export class GridStrategy {
    constructor() {
        this.activeGrids = new Map(); // gridId -> gridState
        this.gridHistory = []; // Completed/stopped grids
        this._isStarting = false; // Mutex to prevent double-start
        this._assetLocks = new Set(); // Asset-specific locks to prevent duplicate grids per asset
        this._stoppingAssets = new Set(); // Assets currently being stopped
    }

    // Helper to get ChaseUtils (may not be loaded immediately)
    get utils() {
        return window.ChaseUtils;
    }

    /**
     * Start a new grid strategy
     * @param {Object} config
     * @param {string} config.asset - Asset symbol
     * @param {number} config.levels - Number of levels per side (1-3)
     * @param {number} config.baseTickDistance - Base tick distance (levels multiply: 10, 20, 30)
     * @param {number} config.sizePerLevel - Size for each order
     * @param {number} config.upperRange - Upper price bound (optional)
     * @param {number} config.lowerRange - Lower price bound (optional)
     * @param {Object} config.chaseSettings - frequencyRangeMin, frequencyRangeMax
     */
    async startGrid(config) {
        // IMPROVED MUTEX: Check both global and asset-specific locks
        if (this._isStarting || this._assetLocks.has(config.asset)) {
            // console.log('[GRID] startGrid already in progress, ignoring duplicate call');
            return { success: true, duplicate: true };  // Silent ignore, no error
        }

        // Check if this asset is currently being stopped
        if (this._stoppingAssets.has(config.asset)) {
            return { success: false, error: 'Grid is currently being stopped for this asset' };
        }

        // Check for existing active grid on this asset
        const existingGrid = Array.from(this.activeGrids.values())
            .find(g => g.asset === config.asset && (g.status === 'active' || g.status === 'stopping'));
        if (existingGrid) {
            // FIX: Use fresh API call to verify orders instead of cached data
            // The position tracker can lag ~30 seconds after manual cancellation,
            // causing false "grid already running" errors
            let activeOrderCount = 0;
            try {
                const freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                const gridOids = existingGrid.orders?.map(o => String(o.oid)) || [];
                activeOrderCount = freshOrders.filter(o =>
                    o.coin === config.asset && gridOids.includes(String(o.oid))
                ).length;
                console.log(`[GRID] Fresh API check: ${activeOrderCount} active orders for grid ${existingGrid.gridId}`);
            } catch (e) {
                // Fallback to cached data if API fails
                activeOrderCount = existingGrid.orders?.filter(o => o.status === 'active').length || 0;
                console.warn(`[GRID] API check failed, using cached count: ${activeOrderCount}`);
            }

            const gridAge = Date.now() - existingGrid.startTime;
            const isStale = activeOrderCount === 0 && gridAge > 60 * 1000; // No active orders + 1 min old (reduced from 5)

            if (isStale || activeOrderCount === 0) {
                console.warn(`[GRID] Removing stale grid ${existingGrid.gridId} (${activeOrderCount} active orders, age: ${Math.round(gridAge/60000)}min)`);
                this._cleanupGrid(existingGrid.gridId);
                this.activeGrids.delete(existingGrid.gridId);
                this._assetLocks.delete(config.asset);
                this._stoppingAssets.delete(config.asset);
                this._saveState();
                // Continue with new grid start
            } else {
                // Grid is legitimately running - block new grid
                return { success: false, error: `Grid already running for ${config.asset}: ${existingGrid.gridId} (${activeOrderCount} active orders)` };
            }
        }

        // Validate config inputs before acquiring locks
        const validation = this._validateConfig(config);
        if (!validation.valid) {
            return { success: false, error: validation.error };
        }

        // Set BOTH locks immediately (synchronous)
        this._isStarting = true;
        this._assetLocks.add(config.asset);

        let gridId = null;

        try {
            // Validate percentage distance (max 20% total)
            if (config.isPercent && config.basePercentDistance) {
                const maxPercent = config.basePercentDistance * config.levels;
                if (maxPercent > 20) {
                    return {
                        success: false,
                        error: `Grid too wide: ${maxPercent.toFixed(1)}% exceeds 20% maximum`
                    };
                }
            }

            // Validate margin for total grid exposure
            const marginCheck = await this._validateGridMargin(config);
            if (!marginCheck.valid) {
                return {
                    success: false,
                    error: marginCheck.error,
                    marginRequired: marginCheck.marginRequired,
                    available: marginCheck.available
                };
            }

            gridId = `grid-${Date.now()}`;

            const gridState = {
                gridId,
                asset: config.asset,
                levels: config.levels,
                baseTickDistance: config.baseTickDistance,
                basePercentDistance: config.basePercentDistance,
                isPercent: config.isPercent || false,
                sizePerLevel: config.sizePerLevel,
                anchor: config.anchor,  // Long below anchor, Short above anchor
                chaseSettings: config.chaseSettings,
                orders: [], // { side, level, tickDistance/percentDistance, oid, chaseId, status }
                status: 'active',
                startTime: Date.now()
            };

            // console.log(`[GRID] Starting grid ${gridId} for ${config.asset}`);
            // console.log(`[GRID] Levels: ${config.levels}, Base tick: ${config.baseTickDistance}, Size: ${config.sizePerLevel}`);

            // Get current best bid/ask for price calculation
            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(config.asset);
            if (!bidAsk || !bidAsk.bestBid || !bidAsk.bestAsk) {
                return { success: false, error: 'Failed to get market prices' };
            }

            const bestBid = bidAsk.bestBid;
            const bestAsk = bidAsk.bestAsk;

            // Get tick size using exchange metadata (fixes scientific notation bug)
            const tickSize = await this._getTickSize(config.asset, bestBid, bestAsk);

            // Build all orders for batch placement
            const ordersToPlace = [];
            const orderMeta = []; // Track metadata for each order

            const anchor = config.anchor || bidAsk.mid;  // Default to mid price if no anchor

            // Validate anchor price is within ±10% of current mid
            if (anchor <= 0 || isNaN(anchor)) {
                return { success: false, error: 'Invalid anchor price' };
            }
            if (!bidAsk.mid || bidAsk.mid <= 0) {
                return { success: false, error: 'Invalid market price for anchor validation' };
            }
            const anchorDeviation = Math.abs(anchor - bidAsk.mid) / bidAsk.mid;
            if (anchorDeviation > 0.10) {
                return {
                    success: false,
                    error: `Anchor price ${anchor.toFixed(2)} is ${(anchorDeviation * 100).toFixed(1)}% from market (${bidAsk.mid.toFixed(2)}). Max allowed: 10%`
                };
            }
            // console.log(`[GRID] Anchor price: ${anchor}`);

            for (let level = 1; level <= config.levels; level++) {
                let buyPrice, sellPrice;
                let distanceValue; // For passing to chase settings

                if (config.isPercent && config.basePercentDistance) {
                    // Percentage mode: calculate price offset as percentage
                    const percentDistance = config.basePercentDistance * level;
                    distanceValue = percentDistance;
                    buyPrice = window.BrowserHyperliquidClient.roundToTickSize(bestBid * (1 - percentDistance / 100));
                    sellPrice = window.BrowserHyperliquidClient.roundToTickSize(bestAsk * (1 + percentDistance / 100));
                } else {
                    // Tick mode: calculate price offset in ticks
                    const tickDistance = config.baseTickDistance * level;
                    distanceValue = tickDistance;
                    buyPrice = window.BrowserHyperliquidClient.roundToTickSize(bestBid - (tickDistance * tickSize));
                    sellPrice = window.BrowserHyperliquidClient.roundToTickSize(bestAsk + (tickDistance * tickSize));
                }

                // FIX: Removed anchor constraint from initial placement
                // The chase tracker already enforces anchor constraint (chase-tracker.js:1178-1189)
                // which prevents orders from CHASING past the anchor during modifications.
                // Constraining at placement was causing "missing orders" near the anchor.

                // BUY order
                ordersToPlace.push({
                    asset: config.asset,
                    side: 'buy',
                    price: buyPrice,
                    size: config.sizePerLevel,
                    reduceOnly: false
                });
                orderMeta.push({
                    side: 'buy',
                    level,
                    tickDistance: config.isPercent ? null : distanceValue,
                    percentDistance: config.isPercent ? distanceValue : null,
                    isPercent: config.isPercent,
                    price: buyPrice
                });

                // SELL order
                ordersToPlace.push({
                    asset: config.asset,
                    side: 'sell',
                    price: sellPrice,
                    size: config.sizePerLevel,
                    reduceOnly: false
                });
                orderMeta.push({
                    side: 'sell',
                    level,
                    tickDistance: config.isPercent ? null : distanceValue,
                    percentDistance: config.isPercent ? distanceValue : null,
                    isPercent: config.isPercent,
                    price: sellPrice
                });
            }

            // console.log(`[GRID] Batch placing ${ordersToPlace.length} orders...`);

            // Batch place all orders at once with 30s timeout
            // If timeout fires, check for orphaned orders and cancel them
            let batchResult;
            try {
                batchResult = await Promise.race([
                    window.BrowserHyperliquidClient.batchPlace(ordersToPlace),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('BATCH_TIMEOUT')), 30000)
                    )
                ]);
            } catch (error) {
                if (error.message === 'BATCH_TIMEOUT') {
                    // Post-timeout cleanup: check for orphaned orders and cancel them
                    console.warn('[GRID] Batch timed out, checking for orphaned orders...');
                    try {
                        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait for potential completion
                        const openOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                        const recentOrders = openOrders?.filter(o =>
                            o.coin === config.asset &&
                            Date.now() - (o.timestamp || 0) < 60000 // Orders placed in last 60s
                        ) || [];

                        // Match ONLY by exact size (safer than price matching)
                        for (const orphan of recentOrders) {
                            const exactMatch = ordersToPlace.find(p =>
                                Math.abs(parseFloat(orphan.sz) - p.size) < 0.0000001
                            );
                            if (exactMatch) {
                                console.warn(`[GRID] Cancelling orphaned order ${orphan.oid}`);
                                try {
                                    await window.BrowserHyperliquidClient.cancelOrder({
                                        orderId: orphan.oid,
                                        asset: config.asset
                                    });
                                } catch (e) {
                                    console.error(`[GRID] Failed to cancel orphan ${orphan.oid}:`, e);
                                }
                            }
                        }
                    } catch (cleanupError) {
                        console.error('[GRID] Orphan cleanup failed:', cleanupError);
                    }
                    // Return early after timeout - do not continue with undefined batchResult
                    return { success: false, error: 'Batch order placement timed out' };
                }
                throw error; // Re-throw non-timeout errors to trigger the existing catch block
            }

            // Parse results and extract OIDs
            const statuses = batchResult?.response?.data?.statuses || [];
            const errors = [];

            // Wait for orders to settle on exchange before starting chases
            await new Promise(resolve => setTimeout(resolve, 500));

            for (let i = 0; i < orderMeta.length; i++) {
                // Check if stop was requested during order placement
                if (gridState.status === 'stopping' || this._stoppingAssets.has(config.asset)) {
                    console.warn('[GRID] Stop requested during order placement, aborting remaining orders');
                    errors.push('Grid stop requested');
                    break;
                }

                const meta = orderMeta[i];
                const status = statuses[i];

                const oid = status?.resting?.oid || status?.filled?.oid;
                if (oid) {
                    // Verify order exists on exchange before starting chase
                    const orderExists = await this._verifyOrderExists(oid, config.asset, meta.side, config.sizePerLevel);
                    if (!orderExists) {
                        console.warn(`[GRID] Order ${oid} not found after placement, skipping chase`);
                        errors.push(`${meta.side.toUpperCase()} L${meta.level}: Order not confirmed`);
                        continue;  // Skip this order, don't start chase
                    }

                    // Start chase for this order with anchor constraint
                    // Anchor constraint: BUY orders cannot cross above anchor, SELL orders cannot cross below anchor
                    const chase = await this._startChaseForOrder(
                        { oid, price: meta.price, side: meta.side, size: config.sizePerLevel },
                        config.asset,
                        {
                            ...config.chaseSettings,
                            tickDistance: meta.tickDistance,
                            percentDistance: meta.percentDistance,
                            isPercent: meta.isPercent,
                            anchorPrice: config.anchor,
                            useAnchor: config.anchor !== null && config.anchor !== undefined
                        }
                    );

                    // If chase failed to start, cancel the orphaned order
                    if (!chase?.success) {
                        console.warn(`[GRID] Chase failed for order ${oid}, cancelling orphaned order`);
                        try {
                            await window.BrowserHyperliquidClient.cancelOrder({
                                orderId: oid,
                                asset: config.asset
                            });
                        } catch (e) {
                            console.error(`[GRID] Failed to cancel orphaned order ${oid}:`, e);
                        }
                        errors.push(`${meta.side.toUpperCase()} L${meta.level}: Chase failed, order cancelled`);
                        continue;  // Don't add to gridState.orders
                    }

                    // Only add to orders if chase succeeded
                    gridState.orders.push({
                        side: meta.side,
                        level: meta.level,
                        tickDistance: meta.tickDistance,
                        percentDistance: meta.percentDistance,
                        isPercent: meta.isPercent,
                        oid: oid,
                        price: meta.price,
                        chaseId: chase.chaseId,
                        status: 'active'
                    });
                } else {
                    const errorMsg = status?.error || 'Unknown error';
                    errors.push(`${meta.side.toUpperCase()} L${meta.level}: ${errorMsg}`);
                }
            }

            // Check for partial failure and handle appropriately
            const activeOrders = gridState.orders.filter(o => o.status === 'active');
            const successCount = activeOrders.length;
            const failCount = errors.length;
            const totalOrders = orderMeta.length;

            // If ALL orders failed, cleanup and return error
            if (successCount === 0) {
                // Attempt cleanup of any orders that might be in limbo
                for (const order of gridState.orders) {
                    if (order.oid) {
                        try {
                            await window.BrowserHyperliquidClient.cancelOrder({
                                orderId: order.oid,
                                asset: config.asset
                            });
                        } catch (e) {
                            // Ignore cleanup errors
                        }
                    }
                }
                const errorMsg = errors.length > 0 ? errors[0] : 'Failed to place any orders';
                return { success: false, error: errorMsg };
            }

            // If PARTIAL failure (some orders placed, some failed), warn but continue
            if (failCount > 0 && successCount > 0) {
                console.warn(`[GRID] Partial grid: ${successCount}/${totalOrders} orders placed`);

                // Dispatch warning event for UI
                window.dispatchEvent(new CustomEvent('grid-partial-start', {
                    detail: {
                        gridId,
                        successCount,
                        failCount,
                        totalOrders,
                        errors: errors.slice(0, 3)  // First 3 errors
                    }
                }));
            }

            this.activeGrids.set(gridId, gridState);
            this._saveState();

            // Start monitoring for fills
            this._startGridMonitoring(gridId);

            // console.log(`[GRID] Grid started: ${gridState.orders.length} orders placed`);

            // Dispatch event
            window.dispatchEvent(new CustomEvent('grid-started', {
                detail: { gridId, ordersPlaced: gridState.orders.length }
            }));

            return { success: true, gridId, ordersPlaced: gridState.orders.length };

        } catch (error) {
            // console.error('[GRID] Error starting grid:', error);
            // Cleanup on error
            if (gridId) {
                this._cleanupGrid(gridId);
                this.activeGrids.delete(gridId);
            }
            return { success: false, error: error.message };
        } finally {
            // ALWAYS release both locks, even if there's an unexpected error
            this._isStarting = false;
            this._assetLocks.delete(config.asset);
        }
    }

    async _placeGridOrder(asset, side, size, tickDistance) {
        try {
            // Get current best bid/ask for price calculation
            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(asset);
            if (!bidAsk || !bidAsk.bestBid || !bidAsk.bestAsk) {
                // console.error('[GRID] Invalid bid/ask data');
                return { success: false, error: 'Invalid bid/ask data' };
            }

            const bestBid = bidAsk.bestBid;
            const bestAsk = bidAsk.bestAsk;

            // Get tick size using exchange metadata
            const tickSize = await this._getTickSize(asset, bestBid, bestAsk);

            // Calculate price: tickDistance ticks away from best
            let price;
            if (side === 'buy') {
                price = bestBid - (tickDistance * tickSize);
            } else {
                price = bestAsk + (tickDistance * tickSize);
            }

            // Round to tick size
            price = window.BrowserHyperliquidClient.roundToTickSize(price);

            // console.log(`[GRID] Placing ${side} order: ${size} @ ${price} (${tickDistance} ticks from market)`);

            // Place order
            const result = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: side,
                size,
                price,
                orderType: 'limit',
                timeInForce: 'GTC',
                reduceOnly: false
            });

            if (result.success) {
                const oid = result.result?.response?.data?.statuses?.[0]?.resting?.oid;
                if (oid) {
                    return { success: true, order: { oid, price, side, size } };
                }
                // Order might have filled immediately - check for filled status
                const filled = result.result?.response?.data?.statuses?.[0]?.filled;
                if (filled) {
                    // console.log(`[GRID] Order filled immediately: ${filled.oid}`);
                    return { success: true, order: { oid: filled.oid, price, side, size } };
                }
                // console.error('[GRID] No OID in response:', JSON.stringify(result.result));
                return { success: false, error: 'No order ID returned' };
            }

            // console.error('[GRID] Order placement failed:', result.error);
            return { success: false, error: result.error || 'Order placement failed' };

        } catch (error) {
            // console.error('[GRID] Error placing order:', error);
            return { success: false, error: error.message || 'Unknown error' };
        }
    }

    /**
     * Get tick size from exchange metadata (preferred) or fallback to bid/ask parsing
     * Applies both 6-szDecimals rule AND 5 significant figures rule
     */
    async _getTickSize(asset, bestBid, bestAsk) {
        try {
            // Use cached szDecimals from Hyperliquid metadata
            await window.BrowserHyperliquidClient.getAssetIndex(asset);
            const szDecimals = window.BrowserHyperliquidClient.assetSzDecimals;

            if (szDecimals !== undefined && szDecimals !== null) {
                // Hyperliquid formula: base price decimals = 6 - szDecimals
                const priceDecimals = 6 - szDecimals;
                return Math.pow(10, -priceDecimals);
            }
        } catch (e) {
            console.warn('[GRID] Could not get szDecimals, falling back to bid/ask parsing');
        }

        // Fallback to original method (for backwards compatibility)
        return this._calculateTickSizeFallback(bestBid, bestAsk);
    }

    /**
     * Fallback tick size calculation from bid/ask prices
     * WARNING: Fails for scientific notation (1e-5) and whole numbers
     */
    _calculateTickSizeFallback(bestBid, bestAsk) {
        const bidStr = String(bestBid);
        const askStr = String(bestAsk);
        const bidDecimals = bidStr.includes('.') ? bidStr.split('.')[1].length : 0;
        const askDecimals = askStr.includes('.') ? askStr.split('.')[1].length : 0;
        const decimals = Math.max(bidDecimals, askDecimals);
        return Math.pow(10, -decimals);
    }

    /**
     * Verify order exists on exchange before starting chase
     * Uses ChaseUtils for consistent verification with fresh API calls
     */
    async _verifyOrderExists(oid, asset, side, size) {
        // Use ChaseUtils if available for consistent verification
        if (this.utils?.verifyOrderExists) {
            const sideApi = side === 'buy' ? 'B' : (side === 'sell' ? 'A' : side);
            const order = await this.utils.verifyOrderExists(asset, sideApi, size, 2, 300);
            return !!order;
        }

        // Fallback to direct verification
        try {
            const orders = await window.BrowserHyperliquidClient.getUserOpenOrders();

            // Strategy 1: Exact OID match (convert both to strings for type safety)
            const oidStr = String(oid);
            const exactMatch = orders?.some(o => String(o.oid) === oidStr);
            if (exactMatch) return true;

            // Strategy 2: Match by asset + side + size (like chase-tracker does)
            if (asset && side && size) {
                const sideNormalized = side === 'buy' ? 'B' : (side === 'sell' ? 'A' : side);
                const fuzzyMatch = orders?.some(o => {
                    const coinMatch = o.coin === asset;
                    const sideMatch = o.side === sideNormalized;
                    const sizeMatch = Math.abs(parseFloat(o.sz) - parseFloat(size)) / parseFloat(size) < 0.01;
                    return coinMatch && sideMatch && sizeMatch;
                });
                if (fuzzyMatch) return true;
            }

            return false;
        } catch (e) {
            console.warn('[GRID] Order verification failed, assuming exists:', e);
            return true;
        }
    }

    /**
     * Validate config inputs before starting grid
     */
    _validateConfig(config) {
        // Asset is required
        if (!config.asset || typeof config.asset !== 'string') {
            return { valid: false, error: 'Asset is required' };
        }

        // Levels must be 1-10
        const levels = parseInt(config.levels);
        if (isNaN(levels) || levels < 1 || levels > 10) {
            return { valid: false, error: 'Levels must be between 1 and 10' };
        }

        // Size per level must be positive
        const size = parseFloat(config.sizePerLevel);
        if (isNaN(size) || size <= 0) {
            return { valid: false, error: 'Size per level must be greater than 0' };
        }

        // Distance validation
        if (config.isPercent) {
            const pct = parseFloat(config.basePercentDistance);
            if (isNaN(pct) || pct <= 0 || pct > 10) {
                return { valid: false, error: 'Percent distance must be between 0 and 10%' };
            }
        } else {
            const ticks = parseFloat(config.baseTickDistance);
            if (isNaN(ticks) || ticks <= 0 || ticks > 1000) {
                return { valid: false, error: 'Tick distance must be between 1 and 1000' };
            }
        }

        // Chase settings validation
        if (config.chaseSettings) {
            const min = config.chaseSettings.frequencyRangeMin;
            const max = config.chaseSettings.frequencyRangeMax;
            if (min !== undefined && (isNaN(min) || min < 1 || min > 3600)) {
                return { valid: false, error: 'Frequency min must be between 1 and 3600 seconds' };
            }
            if (max !== undefined && (isNaN(max) || max < 1 || max > 3600)) {
                return { valid: false, error: 'Frequency max must be between 1 and 3600 seconds' };
            }
            if (min !== undefined && max !== undefined && min > max) {
                return { valid: false, error: 'Frequency min cannot be greater than max' };
            }
        }

        return { valid: true };
    }

    /**
     * Validate margin for total grid exposure before starting
     */
    async _validateGridMargin(config) {
        try {
            // For HIP-3 assets, check USDH balance; for perps, check cross margin
            const isHip3 = config.asset.includes(':');
            let availableBalance;

            if (isHip3) {
                // HIP-3 uses USDH (separate from perp margin)
                // Get state from the specific DEX
                const dexName = config.asset.split(':')[0];
                const userState = await window.BrowserHyperliquidClient.getUserStateForDex(dexName);
                const crossMarginSummary = userState?.crossMarginSummary;
                availableBalance = parseFloat(crossMarginSummary?.accountValue || 0)
                                 - parseFloat(crossMarginSummary?.totalMarginUsed || 0);
            } else {
                // Regular perps - get full state with crossMarginSummary
                const userState = await window.BrowserHyperliquidClient.getUserState();
                const crossMarginSummary = userState?.crossMarginSummary;
                availableBalance = parseFloat(crossMarginSummary?.accountValue || 0)
                                 - parseFloat(crossMarginSummary?.totalMarginUsed || 0);
            }

            // Get current price for margin calculation
            const bidAsk = await window.BrowserHyperliquidClient.getBestBidAsk(config.asset);
            if (!bidAsk?.mid) {
                return { valid: false, error: 'Cannot get market price' };
            }

            // Get actual leverage from exchange, fallback to 10x
            let leverage = 10;
            try {
                const assetCtx = await window.BrowserHyperliquidClient.getAssetCtx(config.asset);
                leverage = assetCtx?.leverage?.value || 10;
            } catch (e) {
                // Use default leverage
            }

            // Calculate total margin required (worst case: all levels filled on both sides)
            const totalSize = config.sizePerLevel * config.levels * 2;  // Buy + Sell sides
            const marginRequired = (bidAsk.mid * totalSize) / leverage;

            // Add 10% buffer for price movement
            const marginWithBuffer = marginRequired * 1.1;

            if (marginWithBuffer > availableBalance) {
                return {
                    valid: false,
                    error: `Insufficient margin: Need $${marginWithBuffer.toFixed(2)}, have $${availableBalance.toFixed(2)}`,
                    marginRequired: marginWithBuffer,
                    available: availableBalance
                };
            }

            return { valid: true, marginRequired: marginWithBuffer, available: availableBalance };
        } catch (error) {
            // FAIL-CLOSED: Don't allow grid to start if margin check fails
            console.warn('[GRID] Margin validation error:', error);
            return { valid: false, error: 'Unable to verify margin - please try again' };
        }
    }

    /**
     * Cleanup grid event listeners
     */
    _cleanupGrid(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) return;

        // Remove all event listeners
        if (grid.stopListener) {
            window.removeEventListener('chase-stopped', grid.stopListener);
            grid.stopListener = null;
        }
        if (grid.cancelListener) {
            window.removeEventListener('order-cancelled', grid.cancelListener);
            grid.cancelListener = null;
        }
    }

    async _startChaseForOrder(order, asset, settings) {
        try {
            return await window.BrowserChaseTracker.startChase(
                order.oid,
                {
                    coin: asset,
                    side: order.side === 'buy' ? 'B' : 'A',
                    sz: order.size,
                    limitPx: order.price
                },
                settings
            );
        } catch (error) {
            // console.error('[GRID] Error starting chase:', error);
            return { success: false, error: error.message };
        }
    }

    _startGridMonitoring(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) return;

        // Listen for chase-stopped events to track fills
        const chaseListener = (event) => {
            const { chaseId, reason } = event.detail;
            const currentGrid = this.activeGrids.get(gridId);
            if (!currentGrid) return;

            // Find matching order
            const order = currentGrid.orders.find(o => o.chaseId === chaseId);
            if (order) {
                const oldStatus = order.status;
                if (reason === 'order_filled') {
                    order.status = 'filled';
                } else if (reason === 'range_exceeded') {
                    order.status = 'stopped_range';
                } else if (reason === 'user_stopped') {
                    order.status = 'stopped';
                } else {
                    order.status = 'stopped';
                }

                // console.log(`[GRID] Order ${order.side} L${order.level} status: ${oldStatus} -> ${order.status} (${reason})`);

                this._saveState();
                this._checkGridCompletion(gridId);
            }
        };

        // Listen for external order cancellations
        const cancelListener = (event) => {
            const { orderId, reason } = event.detail;
            const currentGrid = this.activeGrids.get(gridId);
            if (!currentGrid) return;

            // Find matching order by OID
            const order = currentGrid.orders.find(o => o.oid === orderId);
            if (order && order.status === 'active') {
                order.status = 'cancelled_external';
                console.log(`[GRID] Order ${order.side} L${order.level} cancelled externally`);

                this._saveState();
                this._checkGridCompletion(gridId);

                // Notify UI
                window.dispatchEvent(new CustomEvent('grid-order-cancelled', {
                    detail: { gridId, order, reason }
                }));
            }
        };

        window.addEventListener('chase-stopped', chaseListener);
        window.addEventListener('order-cancelled', cancelListener);

        // Store both listeners for cleanup
        grid.stopListener = chaseListener;
        grid.cancelListener = cancelListener;

        // console.log(`[GRID] Monitoring started for grid ${gridId}`);
    }

    _checkGridCompletion(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) return;

        const activeOrders = grid.orders.filter(o => o.status === 'active');
        const filledOrders = grid.orders.filter(o => o.status === 'filled');
        const cancelledOrders = grid.orders.filter(o =>
            o.status === 'cancelled' || o.status === 'cancelled_external'
        );

        // console.log(`[GRID] Grid ${gridId}: ${activeOrders.length} active, ${filledOrders.length} filled, ${cancelledOrders.length} cancelled`);

        if (activeOrders.length === 0) {
            // All orders filled, stopped, or cancelled
            grid.status = filledOrders.length > 0 ? 'completed' : 'cancelled';
            grid.endTime = Date.now();

            // Cleanup BOTH listeners
            if (grid.stopListener) {
                window.removeEventListener('chase-stopped', grid.stopListener);
                grid.stopListener = null;
            }
            if (grid.cancelListener) {
                window.removeEventListener('order-cancelled', grid.cancelListener);
                grid.cancelListener = null;
            }

            // Move to history (limit to 50 in-memory to prevent leak)
            this.gridHistory.push(grid);
            if (this.gridHistory.length > 50) {
                this.gridHistory = this.gridHistory.slice(-50);
            }
            this.activeGrids.delete(gridId);

            // console.log(`[GRID] Grid ${gridId} ${grid.status}: ${filledOrders.length} orders filled`);

            // Dispatch event
            window.dispatchEvent(new CustomEvent('grid-completed', {
                detail: { gridId, orders: grid.orders, status: grid.status }
            }));

            this._saveState();
        }
    }

    async stopGrid(gridId) {
        const grid = this.activeGrids.get(gridId);
        if (!grid) {
            // console.error(`[GRID] Grid not found: ${gridId}`);
            return { success: false, error: 'Grid not found' };
        }

        // Prevent double-stop
        if (grid.status === 'stopping' || grid.status === 'stopped') {
            return { success: true, alreadyStopping: true };
        }

        // Mark grid and asset as stopping to prevent race conditions
        grid.status = 'stopping';
        this._stoppingAssets.add(grid.asset);

        try {
            // CRITICAL: Remove event listeners FIRST to prevent race conditions
            // (listeners could fire during stop and cause conflicts)
            if (grid.stopListener) {
                window.removeEventListener('chase-stopped', grid.stopListener);
                grid.stopListener = null;
            }
            if (grid.cancelListener) {
                window.removeEventListener('order-cancelled', grid.cancelListener);
                grid.cancelListener = null;
            }

            // Stop all active chases (this also cancels orders via chase tracker)
            for (const order of grid.orders) {
                if (order.status === 'active' && order.chaseId) {
                    try {
                        await window.BrowserChaseTracker.stopChase(order.chaseId, 'user_stopped');
                        order.status = 'stopped';  // Mark as stopped by chase
                    } catch (e) {
                        console.warn(`[GRID] Failed to stop chase ${order.chaseId}:`, e);
                    }
                }
            }

            // Cancel orders that DON'T have a chase (chase already cancelled its own orders)
            // This prevents double-cancellation
            for (const order of grid.orders) {
                if (order.status === 'active' && order.oid && !order.chaseId) {
                    try {
                        await window.BrowserHyperliquidClient.cancelOrder({
                            orderId: order.oid,
                            asset: grid.asset
                        });
                        order.status = 'cancelled';
                    } catch (e) {
                        console.warn(`[GRID] Failed to cancel order ${order.oid}:`, e);
                        order.status = 'cancel_failed';
                    }
                }
            }

            grid.status = 'stopped';
            grid.endTime = Date.now();

            // Move to history (limit to 50 in-memory to prevent leak)
            this.gridHistory.push(grid);
            if (this.gridHistory.length > 50) {
                this.gridHistory = this.gridHistory.slice(-50);
            }
            this.activeGrids.delete(gridId);

            this._saveState();

            // Dispatch event
            window.dispatchEvent(new CustomEvent('grid-stopped', {
                detail: { gridId }
            }));

            return { success: true };
        } finally {
            // CRITICAL: Always clear asset locks, even on error
            this._assetLocks.delete(grid.asset);
            this._stoppingAssets.delete(grid.asset);
        }
    }

    /**
     * Stop all grids for a specific asset (useful when switching symbols)
     */
    async stopAllGridsForAsset(asset) {
        const gridsToStop = [];
        for (const [gridId, grid] of this.activeGrids) {
            if (grid.asset === asset && grid.status === 'active') {
                gridsToStop.push(gridId);
            }
        }

        const results = await Promise.all(
            gridsToStop.map(gridId => this.stopGrid(gridId))
        );

        return {
            stopped: gridsToStop.length,
            results
        };
    }

    /**
     * Force cleanup all stale state (called on page unload or manual reset)
     */
    async forceCleanup() {
        // Collect stopChase promises BEFORE clearing locks
        const stopPromises = [];

        // Stop all running chases and clear listeners from active grids
        for (const [gridId, grid] of this.activeGrids) {
            // Stop running chases for this grid's orders
            for (const order of grid.orders || []) {
                if (order.chaseId && order.status === 'active') {
                    stopPromises.push(
                        window.BrowserChaseTracker?.stopChase(order.chaseId, 'grid_force_cleanup')
                            .catch(e => console.warn(`[GRID] Error stopping chase ${order.chaseId}:`, e.message))
                    );
                }
            }

            if (grid.stopListener) {
                window.removeEventListener('chase-stopped', grid.stopListener);
            }
            if (grid.cancelListener) {
                window.removeEventListener('order-cancelled', grid.cancelListener);
            }
        }

        // Wait for all chases to stop
        if (stopPromises.length > 0) {
            await Promise.allSettled(stopPromises);
        }

        // Clear all locks AFTER async operations complete
        this._isStarting = false;
        this._assetLocks.clear();
        this._stoppingAssets.clear();

        // Clear state
        this.activeGrids.clear();
        this._saveState();

        console.log('[GRID] Force cleanup complete');
    }

    getStatus() {
        const grids = {};
        for (const [gridId, grid] of this.activeGrids) {
            grids[gridId] = {
                asset: grid.asset,
                status: grid.status,
                levels: grid.levels,
                orders: grid.orders.map(o => ({
                    side: o.side,
                    level: o.level,
                    status: o.status,
                    tickDistance: o.tickDistance,
                    percentDistance: o.percentDistance,
                    isPercent: o.isPercent
                })),
                activeCount: grid.orders.filter(o => o.status === 'active').length,
                filledCount: grid.orders.filter(o => o.status === 'filled').length
            };
        }
        return {
            activeGrids: grids,
            count: this.activeGrids.size
        };
    }

    getActiveGrids() {
        return Array.from(this.activeGrids.values());
    }

    _saveState() {
        try {
            const data = {};
            for (const [gridId, grid] of this.activeGrids) {
                // Exclude non-serializable listener functions
                const { stopListener, cancelListener, ...serializable } = grid;
                data[gridId] = serializable;
            }
            localStorage.setItem('activeGrids', JSON.stringify(data));

            // Save history (keep last 20)
            const recentHistory = this.gridHistory.slice(-20);
            localStorage.setItem('gridHistory', JSON.stringify(recentHistory));
        } catch (error) {
            // console.error('[GRID] Error saving state:', error);
        }
    }

    async restoreState() {
        try {
            const saved = localStorage.getItem('activeGrids');
            if (saved) {
                const data = JSON.parse(saved);
                for (const [gridId, grid] of Object.entries(data)) {
                    // Validate required fields before restoring
                    if (!this._isValidGridState(grid)) {
                        console.warn(`[GRID] Skipping invalid grid state: ${gridId}`);
                        continue;
                    }

                    if (grid.status === 'active') {
                        // Verify orders still exist on exchange before restoring
                        const verifiedGrid = await this._verifyGridOrdersOnExchange(grid);
                        const activeOrders = verifiedGrid.orders.filter(o => o.status === 'active');

                        if (activeOrders.length > 0) {
                            this.activeGrids.set(gridId, verifiedGrid);
                            this._startGridMonitoring(gridId);
                            console.log(`[GRID] Restored grid ${gridId}: ${activeOrders.length} verified active orders`);
                        } else {
                            console.warn(`[GRID] Grid ${gridId} has no active orders on exchange, marking completed`);
                            verifiedGrid.status = 'completed';
                            verifiedGrid.endTime = Date.now();
                            this.gridHistory.push(verifiedGrid);
                        }
                    }
                }
            }

            const history = localStorage.getItem('gridHistory');
            if (history) {
                const parsedHistory = JSON.parse(history);
                // Validate and filter history entries
                this.gridHistory = Array.isArray(parsedHistory)
                    ? parsedHistory.filter(g => this._isValidGridState(g)).slice(-50)
                    : [];
            }

            this._saveState();
        } catch (error) {
            console.warn('[GRID] Error restoring state, clearing corrupted data:', error);
            // Clear corrupted state
            localStorage.removeItem('activeGrids');
            localStorage.removeItem('gridHistory');
        }
    }

    /**
     * Verify grid orders still exist on exchange (for restore after page refresh)
     * Uses ChaseUtils for position verification to avoid false "filled" status
     */
    async _verifyGridOrdersOnExchange(grid) {
        try {
            const openOrders = await Promise.race([
                window.BrowserHyperliquidClient.getUserOpenOrders(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
            ]);

            const openOids = new Set(openOrders?.map(o => o.oid) || []);
            let verified = 0, filled = 0, cancelled = 0;

            for (const order of grid.orders) {
                if (order.status === 'active') {
                    if (!openOids.has(order.oid)) {
                        // Order no longer exists - verify position before marking as filled
                        // FIX: Don't assume order gone = filled (could be cancelled)
                        const direction = order.side === 'buy' ? 'LONG' : 'SHORT';
                        let positionExists = false;

                        if (this.utils?.verifyPositionExists) {
                            positionExists = await this.utils.verifyPositionExists(grid.asset, direction);
                        } else {
                            // Fallback: check position directly
                            try {
                                const positions = await window.BrowserHyperliquidClient.getUserPositions();
                                const pos = positions?.find(p => p.position?.coin === grid.asset);
                                const posSize = parseFloat(pos?.position?.szi || 0);
                                positionExists = Math.abs(posSize) > 0;
                            } catch (e) {
                                // Assume filled on error (conservative)
                                positionExists = true;
                            }
                        }

                        if (positionExists) {
                            order.status = 'filled';
                            filled++;
                        } else {
                            order.status = 'cancelled';
                            cancelled++;
                            console.warn(`[GRID] Order ${order.oid} missing with no position - marked cancelled`);
                        }

                        // Stop the chase if still running
                        if (order.chaseId) {
                            try {
                                await window.BrowserChaseTracker?.stopChase(order.chaseId, 'order_not_found');
                            } catch (e) {
                                // Ignore
                            }
                        }
                    } else {
                        verified++;
                    }
                }
            }

            console.log(`[GRID] ${grid.gridId}: ${verified} verified, ${filled} filled, ${cancelled} cancelled`);
            return grid;
        } catch (error) {
            console.warn('[GRID] Verification failed:', error.message);
            // FAIL-CLOSED: Mark all orders as unknown status on verification failure
            // This prevents zombie grids with phantom orders
            for (const order of grid.orders) {
                if (order.status === 'active') {
                    order.status = 'verification_failed';
                }
            }
            return grid;
        }
    }

    /**
     * Validate grid state has required fields
     */
    _isValidGridState(grid) {
        if (!grid || typeof grid !== 'object') return false;
        if (!grid.gridId || typeof grid.gridId !== 'string') return false;
        if (!grid.asset || typeof grid.asset !== 'string') return false;
        if (!Array.isArray(grid.orders)) return false;
        if (typeof grid.status !== 'string') return false;
        return true;
    }
}

// Create singleton instance (only if not already created)
if (typeof window !== 'undefined') {
    if (!window.GridStrategy) {
        window.GridStrategy = new GridStrategy();
        // restoreState is async - call it and let it run
        window.GridStrategy.restoreState().catch(e => {
            console.error('[GRID] Error during restoreState:', e);
        });
        // console.log('[GRID] GridStrategy initialized');
    }
}
